import { useState } from 'react';
import { aiTemplate, createTemplate, type OutreachCategory, type AiTemplateResult } from '@/lib/api/outreach';
import { useToast } from '@/lib/hooks/useToast';

interface Props {
  categories: OutreachCategory[];
  onClose: () => void;
  onSaved: () => void;
}

export function AiTemplateModal({ categories, onClose, onSaved }: Props) {
  const { toast } = useToast();
  const [kind, setKind] = useState<'email' | 'sms'>('email');
  const [purpose, setPurpose] = useState('');
  const [category, setCategory] = useState('');
  const [tone, setTone] = useState('warm, direct, no fluff');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<AiTemplateResult | null>(null);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    if (!purpose.trim()) { toast.error('Describe what the template is for'); return; }
    setGenerating(true);
    try {
      setResult(await aiTemplate({ kind, purpose, category, tone }));
    } catch (e) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await createTemplate({
        title: `${result.title}${kind === 'sms' ? ' (SMS)' : ''}`,
        category: category || undefined,
        subject: result.subject,
        body: result.body,
      });
      toast.success('Template saved');
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-primary">auto_awesome</span>
            New template with AI
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div className="flex gap-2">
            {(['email', 'sms'] as const).map(k => (
              <button key={k} onClick={() => { setKind(k); setResult(null); }}
                className={`h-9 px-4 rounded-lg text-sm font-semibold border ${kind === k ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300'}`}>
                {k === 'email' ? 'Email' : 'SMS'}
              </button>
            ))}
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-500">What's it for?</span>
            <textarea value={purpose} onChange={e => setPurpose(e.target.value)} rows={3}
              placeholder="e.g. Introduce our deck-building service to property managers with aging rental units"
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-500">Category (optional)</span>
              <select value={category} onChange={e => setCategory(e.target.value)} className="h-9 px-2 rounded-lg border border-gray-300 text-sm">
                <option value="">—</option>
                {categories.filter(c => c.id !== 'All').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-500">Tone</span>
              <input value={tone} onChange={e => setTone(e.target.value)} className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
            </label>
          </div>

          <button onClick={generate} disabled={generating}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
            <span className={`material-symbols-outlined text-base ${generating ? 'animate-spin' : ''}`}>{generating ? 'progress_activity' : 'auto_awesome'}</span>
            {generating ? 'Writing…' : result ? 'Regenerate' : 'Generate'}
          </button>

          {result && (
            <div className="mt-2 rounded-xl border border-gray-200 p-3 space-y-2 bg-gray-50">
              <p className="text-xs text-gray-500">Title: <span className="font-medium text-gray-800">{result.title}</span></p>
              {result.subject !== undefined && (
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Subject</span>
                  <input value={result.subject} onChange={e => setResult({ ...result, subject: e.target.value })}
                    className="h-9 px-3 rounded-lg border border-gray-300 text-sm bg-white" />
                </label>
              )}
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Body {kind === 'sms' && `(${result.body.length} chars)`}</span>
                <textarea value={result.body} onChange={e => setResult({ ...result, body: e.target.value })}
                  rows={kind === 'sms' ? 4 : 12} className="px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white leading-relaxed" />
              </label>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="h-9 px-4 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={save} disabled={!result || saving}
            className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save as template'}
          </button>
        </div>
      </div>
    </div>
  );
}
